import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon, Warning as WarningIcon } from '@mui/icons-material';

interface ApplyEnterpriseContextModalProps {
  open: boolean;
  handleConfirm: () => void;
  onClose: () => void;
  title?: string;
  message?: string;
  type?: 'taxonomy' | 'solutionxplr';
}

export const ApplyEnterpriseContextModal: React.FC<ApplyEnterpriseContextModalProps> = ({
  open,
  onClose,
  handleConfirm,
  title = 'Generate Diagram',
  message = 'This action will generate a technology landscape diagram. Do you want to continue?',
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          padding: 0,
          overflow: 'hidden',
          boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: '#4285f4',
          color: 'white',
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '18px' }}>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            p: 0.5,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 4, pt: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          {/* Warning Icon */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: '50%',

              color: '#856404',
              flexShrink: 0,
              mt: 0.5,
            }}
          >
            <WarningIcon sx={{ fontSize: 24 }} />
          </Box>

          {/* Message */}
          <Box sx={{ flex: 1, pt: 1 }}>
            <Typography
              variant="body1"
              sx={{
                fontSize: '16px',
                lineHeight: 1.5,
                color: '#424242',
              }}
            >
              {message}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          p: 3,
          pt: 0,
          gap: 2,
          justifyContent: 'flex-end',

          mt: 1,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            py: 1.5,
            borderColor: '#d1d5db',
            color: '#6b7280',
            '&:hover': {
              borderColor: '#9ca3af',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            py: 1.5,
            backgroundColor: '#4285f4',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#3367d6',
              boxShadow: '0px 2px 8px rgba(66, 133, 244, 0.3)',
            },
          }}
        >
          ✓ Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};
